import React, { useMemo } from 'react';
import {
    useOrderProductionTypeInputQuery,
    useProductInventoryPageQuery,
} from '../../../../services';
import { DPage, DPageBody, DPageHeader, DPageLoader } from '../../../dum';
import { InventoryByProduct } from './blocks/inventory-by-product';
import { InventoryByTotals } from './blocks/inventory-by-totals';
import { InventoryByProductCategory } from './blocks/inventory-by-product-category';
import {
    Box,
    FormControl,
    FormControlLabel,
    MenuItem,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useBoolean, useString } from '../../../../helpers';
import {
    BAG_PRODUCTION_TYPE_ID,
    isInventoryTracked,
} from '../../../../services/entities';

// The selector holds a string so the two non-id choices can persist in local
// storage alongside a real id (useString keeps any non-empty value; a numeric
// hook would have to invent falsy sentinels).
const ALL_TYPES = 'all';
const NO_TYPE = 'none';

export function ProductInventory() {
    const { data, loading } = useProductInventoryPageQuery();
    // Reuses the existing variable-free catalog document instead of adding
    // getOrderProductionTypes to this page's query: same cached result, and it
    // keeps the change off the generated schema file.
    const { data: typesData } = useOrderProductionTypeInputQuery();

    const [isDetailed, setIsDetailed] = useBoolean({
        KEY: ProductInventory.name + 'IsDetailed',
        defaultValue: false,
    });

    // Defaults to corte y empaque (bolsas) — the type that is actually read
    // here day to day. The choice sticks per browser afterwards.
    const [productionType, setProductionType] = useString({
        KEY: ProductInventory.name + 'ProductionType',
        defaultValue: String(BAG_PRODUCTION_TYPE_ID),
    });

    // Only the tracked types can appear in the payload, so offering pellet or
    // lavado would only ever produce an empty page. Same helper the rows are
    // filtered with, so the options and the data can never disagree.
    const productionTypeOptions = useMemo(
        () =>
            (typesData?.getOrderProductionTypes || []).filter((type) =>
                isInventoryTracked(type.id),
            ),
        [typesData],
    );

    // While the catalog is still in flight the stored id matches no MenuItem,
    // which MUI reports as an out-of-range value. Show the field empty for that
    // one frame; the row filter below keeps using the real stored choice.
    const selectValue = useMemo(() => {
        const available = [
            ...productionTypeOptions.map((type) => String(type.id)),
            NO_TYPE,
            ALL_TYPES,
        ];
        return available.includes(productionType) ? productionType : '';
    }, [productionTypeOptions, productionType]);

    // The backend already scopes getProductsInventory to the tracked types
    // (corte y empaque, extrusión, uncategorised) and to vigentes. Re-filter
    // through the shared helper rather than a bare `=== 1` so this page shows
    // exactly the same set the balance is defined for — including extrusión,
    // whose consumption leg now gives it a real number — and never a stale
    // type-1-only view. The selector then narrows that set further.
    const productsInventory = useMemo(
        () =>
            (data?.getProductsInventory || []).filter((pi) => {
                const typeId = pi.product?.order_production_type_id ?? null;

                if (!isInventoryTracked(typeId)) {
                    return false;
                }
                if (productionType === ALL_TYPES) {
                    return true;
                }
                if (productionType === NO_TYPE) {
                    return typeId === null;
                }
                return typeId === Number(productionType);
            }),
        [data, productionType],
    );

    return (
        <DPage>
            <DPageHeader title={'Inventario'}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <TextField
                        select
                        label={'Tipo de producción'}
                        size={'small'}
                        margin={'none'}
                        value={selectValue}
                        onChange={(e) => {
                            setProductionType(e.target.value);
                        }}
                        sx={{ width: 190 }}
                    >
                        {productionTypeOptions.map((type) => (
                            <MenuItem key={type.id} value={String(type.id)}>
                                {type.name}
                            </MenuItem>
                        ))}
                        <MenuItem value={NO_TYPE}>Sin tipo</MenuItem>
                        <MenuItem value={ALL_TYPES}>Todos</MenuItem>
                    </TextField>
                    <FormControl>
                        <FormControlLabel
                            labelPlacement={'end'}
                            control={
                                <Switch
                                    onChange={() => {
                                        setIsDetailed(!isDetailed);
                                    }}
                                    checked={isDetailed}
                                />
                            }
                            label={'Descripción detallada'}
                        />
                    </FormControl>
                </Box>
            </DPageHeader>
            <DPageBody disableGutters>
                {loading ? (
                    <DPageLoader />
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            // Single scroll container anchored to the page-body
                            // flex height (same pattern as "Productos por producir").
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                        }}
                    >
                        <InventoryByProduct
                            isDetailed={isDetailed}
                            productsInventory={productsInventory}
                        />
                        <InventoryByTotals
                            productsInventory={productsInventory}
                        />
                        <InventoryByProductCategory
                            productsInventory={productsInventory}
                        />
                        {/* Footnote: names the scope so an absent product reads
                            as out of scope, not as a data error. Pellet and
                            lavado capture no consumption, so no honest balance
                            exists for them and they are deliberately excluded. */}
                        <Typography
                            variant={'caption'}
                            color={'text.secondary'}
                            sx={{ px: 1, pb: 1 }}
                        >
                            El inventario solo se calcula para corte y empaque y
                            extrusión (más productos sin tipo); use el selector
                            de arriba para cambiar entre ellos. Pellet y
                            compactado (lavado) no registran su consumo, así que
                            no tienen un inventario confiable y no aparecen en
                            esta lista.
                        </Typography>
                    </Box>
                )}
            </DPageBody>
        </DPage>
    );
}
